import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { catchError, map, of, switchMap } from 'rxjs'

import { MessageType } from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { AiConversationService } from '@flowx/designer/modules/ai/conversation/ai-conversation.service'
import { ConversationErrorActions } from '@flowx/designer/modules/ai/conversation/conversation-error.actions'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { mapResource } from '@flowx/designer/shared/entities/application.entity'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { ProjectDataModelActions } from '@flowx/designer/shared/store/actions/project-data-model.actions'
import { selectAllProjectDataModelEntities } from '@flowx/designer/shared/store/selectors/data-model.selectors'

import { AiArchitectActions } from './architect.actions'

import { AiArchitectApiService } from '../api/architect.api'
import { AiArchitectService } from '../services/architect.service'

@Injectable()
export class AiArchitectEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #architectApi = inject(AiArchitectApiService)
  #aiConversationService = inject(AiConversationService)
  #toast = inject(ToastMessageService)

  keepOutcome = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.keepOutcome),
      switchMap(() => {
        const proposed = this.#store.selectSignal(
          ConversationSelectors.architectGeneratedDataModel
        )()
        const existing = this.#store.selectSignal(selectAllProjectDataModelEntities)()
        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()

        if (!proposed) {
          const error = new Error('No architect data model outcome found to keep')
          console.error('[Conversation Error] Keep architect outcome failed:', error)
          return of(ConversationErrorActions.keepOutcomeFailed({ error, threadId, conversationId }))
        }

        const payload = AiArchitectService.buildApplyPayload(proposed, existing ?? [])

        return this.#architectApi.applyDataModel(payload).pipe(
          switchMap((response) => {
            const savedNames = mapResource(response).map((dataType) => dataType.name)
            // a proposed-new name missing from the saved set was renamed to avoid a name clash
            const duplicateResourceNames = payload.newResourceNames.filter(
              (name) => !savedNames.includes(name)
            )

            if (!duplicateResourceNames.length) {
              return of(AiArchitectActions.keepOutcomeSuccess({ duplicateResourceNames: [] }))
            }

            this.#toast.showWarningMessage(
              'Some data types were duplicated',
              `These types already existed and were created as duplicates: ${duplicateResourceNames.join(', ')}`
            )

            return this.#aiConversationService
              .addStoreMessage({
                prompt: `These data types already existed and were created as duplicates: ${duplicateResourceNames.join(', ')}.`,
                type: MessageType.SystemMsg,
              })
              .pipe(map(() => AiArchitectActions.keepOutcomeSuccess({ duplicateResourceNames })))
          }),
          catchError((err) => {
            const error = err instanceof Error ? err : new Error('Failed to keep architect outcome')
            console.error('[Conversation Error] Keep architect outcome failed:', error)
            return of(
              ConversationErrorActions.keepOutcomeFailed({ error, threadId, conversationId })
            )
          })
        )
      })
    )
  )

  discardOutcome = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.discardOutcome),
      map(() => AiArchitectActions.discardOutcomeSuccess())
    )
  )

  // The applied types are persisted server-side; reload the stored project data model so the
  // canvas reflects them once the preview overlay clears.
  reloadDataModelAfterKeep = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiArchitectActions.keepOutcomeSuccess),
      map(() => ProjectDataModelActions.loadProjectDataModel())
    )
  )
}
